import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from 'react';
import * as S from './ups-sell.page.style';
import { UpSell } from '../../../common/types/up-sell';
import { useTranslation } from 'react-i18next';
import { navigateExternalNoReferrer } from '../../../common/utils/navigate-no-referer';
import { OnRejectUpSellEnum } from '../../../common/types/enums/on-reject-up-sell.enum';
import { useLocation } from 'react-router-dom';
import {
  getUpsellRecurringSummary,
  resolveUpsellOffer,
} from '../../../common/utils/resolve-upsell-offer';

type Props = {
  upSell?: UpSell;
  isLoading: boolean;
  isProcessing?: boolean;
  hasError?: boolean;
  isSuccess?: boolean;
  onConfirm: () => void;
  setRefused: Dispatch<SetStateAction<boolean>>;
  refused: boolean;
};

export const UpSellPage: React.FC<Props> = ({
  upSell,
  isLoading,
  isProcessing,
  hasError,
  isSuccess,
  onConfirm,
  refused,
  setRefused,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const offer = useMemo(() => resolveUpsellOffer(upSell), [upSell]);
  const preco = useMemo(() => upSell?.precos?.[0], [upSell]);
  const recurringSummary = useMemo(
    () => getUpsellRecurringSummary(offer, t),
    [offer, t],
  );

  // ---------------------------------

  // ---- auto resize do iframe ----
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const sendHeight = () => {
      const body = document.body;
      const html = document.documentElement;

      const height = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0,
      );

      if (window.parent) {
        window.parent.postMessage(
          {
            source: 'VendepayUpsell',
            type: 'HEIGHT',
            height,
          },
          '*',
        );
      }
    };

    sendHeight();

    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver(() => sendHeight())
        : null;

    if (resizeObserver && document.body) {
      resizeObserver.observe(document.body);
    }

    window.addEventListener('load', sendHeight);

    return () => {
      window.removeEventListener('load', sendHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // -------------------------------

  // --- loading state ---
  if (isLoading) {
    return (
      <>
        <S.UpsellGlobalStyle />
        <S.Container>
          <S.SkeletonButton />
          <S.SkeletonButton />
        </S.Container>
      </>
    );
  }

  // --- erro ao carregar ---
  if (hasError) {
    return (
      <>
        <S.UpsellGlobalStyle />
        <S.Container>
          <S.ErrorMessage>
            {t('failedToLoadExtraOffer')}
            <br />
            {t('youMayProceed')}
          </S.ErrorMessage>
        </S.Container>
      </>
    );
  }

  if (isProcessing) {
    return (
      <>
        <S.UpsellGlobalStyle />
        <S.Container>
          <S.ProcessingBox>
            <span className="spinner" />
            <strong>{t('processingPayment')}</strong>
            <p>{t('doNotClosePage')}</p>
          </S.ProcessingBox>
        </S.Container>
      </>
    );
  }

  // --- oferta aceita ---
  if (isSuccess) {
    return (
      <>
        <S.UpsellGlobalStyle />
        <S.Container>
          <S.SuccessBox>
            <div className="checkmark">
              <span className="circle" />
              <span className="tick" />
            </div>
            <strong>{t('offerAdded')}</strong>
            <p>{t('offerAppliedSuccessfully')}</p>
          </S.SuccessBox>
        </S.Container>
      </>
    );
  }

  // --- oferta recusada localmente ---
  if (refused) {
    return (
      <>
        <S.UpsellGlobalStyle />
        <S.Container>
          <S.RefusedBox>
            <strong>Oferta recusada</strong>
            <p>
              Se mudar de ideia, você ainda pode aproveitar esta condição
              especial enquanto sua compra estiver aberta.
            </p>

            <button
              className="recover-offer"
              type="button"
              onClick={() => setRefused(false)}
            >
              Ver oferta novamente
            </button>
          </S.RefusedBox>
        </S.Container>
      </>
    );
  }

  // --- sem preco ---
  if (offer.amount == null || !offer.currency) return null;

  // --- tela principal ---
  return (
    <>
      <S.UpsellGlobalStyle />
      <S.Container>
        {offer.isRecurring && (
          <S.RecurringCard>
            <span className="badge">{t('offerAcquired')}</span>

            {offer.planName && <strong>{offer.planName}</strong>}

            {offer.planDescription && <p>{offer.planDescription}</p>}

            {recurringSummary && (
              <div className="summary">
                <span className="primary">{recurringSummary.primary}</span>
                <span className="secondary">{recurringSummary.secondary}</span>
              </div>
            )}
          </S.RecurringCard>
        )}

        <button
          type="button"
          className="accept"
          style={{ backgroundColor: offer.ctaColorHex ?? preco?.ctaColorHex }}
          onClick={onConfirm}
        >
          {offer.acceptText ?? preco?.acceptText}
        </button>

        <button
          onClick={() => {
            const params = new URLSearchParams(location.search);
            if (upSell?.precos?.[0]?.onReject === OnRejectUpSellEnum.REDIRECIONAR) {
              navigateExternalNoReferrer(upSell?.precos?.[0]?.urlReject ?? '', {
                newTab: false,
                extraParams: params,
              });
              return;
            }
            setRefused(true);
          }}
          type="button"
          className="reject"
        >
          {offer.rejectText ?? preco?.rejectText}
        </button>
      </S.Container>
    </>
  );
};
